import { MouseEvent, ReactNode, useMemo } from 'react';

import { $Color } from '@suntex.core/color';
import { Entities_Player_ProgressSummary_Response } from '@suntex.fim/api-client';
import { GradeLevel } from '@suntex.fim/enums';
import { Collapse, ProgressBar } from '@suntex.fim/ui';

import { rootRadialData } from '../const';
import { stagesOfLearningNode } from '../const/model/views/stages-of-learning';
import { ModuleTagging } from '../types';
import {
  SolStageProgress,
  StagesOfLearningLink,
  buildStagesOfLearningProgress,
} from '../utils/buildStagesOfLearningProgress';
import { createGetHref } from '../utils/getHref';

const PERCENT = 100;
const STAGE_BAR_HEIGHT = 16;
const CHILD_BAR_HEIGHT = 8;
const EMPHASIZED_BACKGROUND_ALPHA = 0.08;
const MIN_STAGE_WIDTH = '220px';

const solNode = rootRadialData.children.find(
  (c) => c.tag === stagesOfLearningNode.tag,
);

function defaultGetHubHref(path: string): string {
  return `#${path}`;
}

function toHex(color: number): `#${string}` {
  return $Color.toHexString($Color.from(color));
}

/**
 * Shows silver star progress for every node of the Stages of Learning: levels
 * are stacked vertically as collapsible panels (only the level matching
 * `gradeLevel` is expanded by default), with each level's stages laid out
 * horizontally.
 */
export function StagesOfLearningProgress({
  config,
  isModuleEnabled,
  gradeLevel,
  progressSummary,
  moduleTagging,
  getHubHref = defaultGetHubHref,
  onNavigate,
}: {
  readonly config: {
    readonly preview: boolean;
    readonly previewModulesPath?: string;
  };
  readonly isModuleEnabled: (moduleSlug: string) => boolean;
  readonly gradeLevel: GradeLevel | undefined;
  readonly progressSummary:
    | Entities_Player_ProgressSummary_Response
    | undefined;
  readonly moduleTagging: readonly ModuleTagging[];
  /** Builds a URL that opens the hub at the given path. Defaults to `#${path}`. */
  readonly getHubHref?: (path: string) => string;
  /**
   * When provided, plain left-clicks on links are handled by this callback
   * (e.g. for client-side routing) instead of the browser.
   */
  readonly onNavigate?: (link: StagesOfLearningLink) => void;
}) {
  const levels = useMemo(() => {
    if (!solNode) return [];
    const getHref = createGetHref(config.preview, config.previewModulesPath);
    return buildStagesOfLearningProgress({
      solNode,
      gradeLevel,
      progressSummary,
      moduleTagging,
      isModuleEnabled,
      getHref: (href) => getHref(href) ?? href,
      getHubHref,
    });
  }, [
    config.preview,
    config.previewModulesPath,
    gradeLevel,
    progressSummary,
    moduleTagging,
    isModuleEnabled,
    getHubHref,
  ]);

  const defaultActiveKey = levels.find(
    (level) => level.gradeLevel === gradeLevel,
  )?.key;

  return (
    <Collapse
      // Remount when the default changes (e.g. the grade loads after the
      // first render) so the matching level is expanded.
      key={defaultActiveKey ?? ''}
      defaultActiveKey={defaultActiveKey}
      panels={levels.map((level) => ({
        key: level.key,
        header: (
          <span style={{ color: toHex(level.color), fontWeight: 'bold' }}>
            {level.label}
          </span>
        ),
        content: (
          <div style={{ overflowX: 'auto' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${level.stages.length}, minmax(${MIN_STAGE_WIDTH}, 1fr))`,
                gap: 16,
              }}
            >
              {level.stages.map((stage) => (
                <StageProgress
                  key={stage.key}
                  stage={stage}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        ),
      }))}
    />
  );
}

function StageProgress({
  stage,
  onNavigate,
}: {
  stage: SolStageProgress;
  onNavigate: ((link: StagesOfLearningLink) => void) | undefined;
}) {
  const color = toHex(stage.color);
  return (
    <div
      style={{
        border: stage.emphasized ? `3px solid ${color}` : '1px solid #d9d9d9',
        borderRadius: 8,
        padding: 12,
        background: stage.emphasized
          ? $Color.toRGBA($Color.from(stage.color), EMPHASIZED_BACKGROUND_ALPHA)
          : undefined,
      }}
    >
      <ProgressBar
        label={
          <span style={{ fontWeight: 'bold' }}>
            <ProgressLink link={stage.link} onNavigate={onNavigate}>
              {stage.label}
            </ProgressLink>
            {stage.emphasized && ' (Goal)'}
          </span>
        }
        percent={stage.progress * PERCENT}
        fill={color}
        height={STAGE_BAR_HEIGHT}
      />
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: '12px 0 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {stage.children.map((child) => (
          <li key={child.key}>
            <ProgressBar
              label={
                <ProgressLink link={child.link} onNavigate={onNavigate}>
                  {child.label}
                </ProgressLink>
              }
              percent={child.progress * PERCENT}
              fill={color}
              height={CHILD_BAR_HEIGHT}
              showData={false}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProgressLink({
  link,
  onNavigate,
  children,
}: {
  link: StagesOfLearningLink;
  onNavigate: ((link: StagesOfLearningLink) => void) | undefined;
  children: ReactNode;
}) {
  return (
    <a
      href={link.href}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        if (onNavigate && isPlainLeftClick(e)) {
          e.preventDefault();
          onNavigate(link);
        }
      }}
    >
      {children}
    </a>
  );
}

function isPlainLeftClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}
